// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { CreateLabelingHelper } from '@project/components/label/helper/create-labeling.helper';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { LabelingUtils } from '../../model3d/util/labeling-utils';
import { ArticleContainerUtils } from '../../model3d/util/article-container-utils';
import { ArticleConnectionService } from '../../model3d/article-connection.service';
import { ContainerService } from '../../model3d/container.service';
import { Connection } from '../../model3d/connection';
import { ConnectionSortGroup } from '../../model3d/connection-sort-group';
import { Rectangle3D } from '../../model/rectangle-3d';
import { Vector3 } from 'three';
import { MathUtils } from '../../common/util/math-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionInfoUtils } from '../../model3d/util/connection-info-utils';
import { CollisionHelper } from '../collision.helper';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '../../model3d/article-container';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { AdvancedLine } from '../../model/advanced-line';
import { ArticleUtils } from '@article/util/article-utils';

@Injectable({ providedIn: 'root' })
export class AcceptingConnectionSortGroupHelper {
  private static readonly EPSILON: number = 0.1;
  public allowAdaptersOverspanningUnconnectedRailArticles = true;

  constructor(
    private mountingRailHelper: MountingRailHelper,
    private labelingUtils: LabelingUtils,
    private articleContainerUtils: ArticleContainerUtils,
    private articleConnectionService: ArticleConnectionService,
    private containerService: ContainerService,
    private createLabelingHelper: CreateLabelingHelper,
  ) {
  }

  public splitConnectionsForPrint(connections: Connection[]) {
    const sortGroups: ConnectionSortGroup[] = [];
    for (const cube of connections) {
      this.addConnectionCubeInSortGroup(cube, sortGroups);
    }

    return sortGroups.map(sortGroup => sortGroup.sortConnections());
  }

  public splitConnectionCubesOnGroups(
    connectionCubes: Connection[],
    isAdapterConnections: boolean = false,
    ignoreDistance: boolean = false,
  ): Connection[][] {
    const sortGroups: ConnectionSortGroup[] = [];
    for (const cube of connectionCubes) {
      this.addConnectionCubeInSortGroup(cube, sortGroups);
    }

    let connectionGroups: Connection[][] = [];
    for (const sortGroup of sortGroups) {
      sortGroup.sortConnections();
      connectionGroups = connectionGroups.concat(this.createConnectionGroups(sortGroup, isAdapterConnections, ignoreDistance));
    }
    return connectionGroups;
  }

  protected addConnectionCubeInSortGroup(connectionCube: Connection, sortGroups: ConnectionSortGroup[]): void {
    const connectionRectangle: Rectangle3D = connectionCube.compositionRectangle;
    const sortDirection: Vector3 = this.getSortDirection(connectionRectangle, connectionCube.articleContainer.article);
    const absUDirection: Vector3 = new Vector3(Math.abs(sortDirection.x), Math.abs(sortDirection.y), Math.abs(sortDirection.z));
    const normal: Vector3 = MathUtils.getNormalFromFirstPoint(connectionRectangle.point1, connectionRectangle.point4, connectionRectangle.point3);

    let fittingGroup: ConnectionSortGroup = null;
    for (const group of sortGroups) {
      if (!MathUtils.equalsNumber3D(sortDirection, group.sortDirection) || !MathUtils.equalsNumber3D(normal, group.normalDirection)) {
        continue;
      }
      const groupFirstPoint: Vector3 = group.connectionFirstPoint;
      if (!this.isSameGeometricRectangles(connectionRectangle, group.connectionFirstRectangle)) {
        continue;
      }
      // connections placed in one group should be aligned by u-direction of accepting connection
      let connectionsDirection: Vector3 = connectionRectangle.point1.clone().sub(groupFirstPoint);
      connectionsDirection.normalize();
      // abs should be used here for considering cases when the first connection cube in a group is not really the first.
      // It happens because cubes are not sorted
      connectionsDirection = new Vector3(Math.abs(connectionsDirection.x), Math.abs(connectionsDirection.y), Math.abs(connectionsDirection.z));
      if (MathUtils.equalsNumber3D(absUDirection, connectionsDirection)) {
        fittingGroup = group;
        break;
      }
    }
    if (!fittingGroup) {
      fittingGroup = new ConnectionSortGroup();
      fittingGroup.sortDirection = sortDirection;
      fittingGroup.normalDirection = normal;
      sortGroups.push(fittingGroup);
    }
    fittingGroup.connectionCubes.push(connectionCube);
  }

  protected getSortDirection(connectionRectangle: Rectangle3D, article: ArticleVO): Vector3 {
    const uDirection: Vector3 = connectionRectangle.point4.clone().sub(connectionRectangle.point1);
    uDirection.normalize();
    return uDirection;
  }

  private createConnectionGroups(
    sortGroup: ConnectionSortGroup,
    isAdapterGroup: boolean,
    ignoreDistance: boolean,
  ): Connection[][] {
    const connectionGroups: Connection[][] = [];
    for (const currentCube of sortGroup.connectionCubes) {
      let isCubeWithoutGroup: boolean = true;
      for (const processingGroup of connectionGroups) {
        const previousCube: Connection = processingGroup[processingGroup.length - 1];
        if (!previousCube || this.isSingleGroupOfConnectionCubeByLocation(previousCube, currentCube, isAdapterGroup, ignoreDistance)) {
          processingGroup.push(currentCube);
          isCubeWithoutGroup = false;
          break;
        }
      }

      if (isCubeWithoutGroup) {
        connectionGroups.push(new Array(currentCube));
      }
    }
    return connectionGroups;
  }

  public isSingleGroupOfConnectionCubeByLocation(
    previousCube: Connection,
    currentCube: Connection,
    isAdapterCubes: boolean,
    ignoreDistance: boolean,
  ): boolean {
    const previousRectangle: Rectangle3D = this.labelingUtils.calculateLabelingRectangle(previousCube);
    const currentRectangle: Rectangle3D = this.labelingUtils.calculateLabelingRectangle(currentCube);

    let isSameGeometricRectangles: boolean = this.isSameGeometricRectangles(previousRectangle, currentRectangle);
    if (MathUtils.equalsRectangle3D(previousRectangle, currentRectangle)) {
      return isSameGeometricRectangles;
    }

    if (this.hasCollidingConnection([previousCube, currentCube])) {
      return false;
    }

    let ignoreDistanceBetweenNeighborConnections: boolean = true;

    // if there is an article anywhere between the connected articles, we want to split the markingstrip on adapters
    if(isAdapterCubes && this.isRailArticlesBetweenConnections(previousCube, currentCube) && !this.allowAdaptersOverspanningUnconnectedRailArticles) {
      return false;
    }

    if (!ignoreDistance) {
      ignoreDistanceBetweenNeighborConnections =
        isAdapterCubes && this.articleContainerUtils.allConnectionsBelongToDifferentContainers([previousCube, currentCube]);
    }
    // check distance between connections
    const distanceBetweenNeighborConnections: number = ConnectionInfoUtils.calculateRectangleDistance(previousRectangle, currentRectangle);
    if (!ignoreDistanceBetweenNeighborConnections && isSameGeometricRectangles &&
      distanceBetweenNeighborConnections > AcceptingConnectionSortGroupHelper.EPSILON) {
      isSameGeometricRectangles = this.isLabelingAcrossOverlapAllowed(previousCube.articleContainer, currentCube.articleContainer) ||
        this.isEndPlatesBetweenArticles(previousCube.articleContainer, currentCube.articleContainer, distanceBetweenNeighborConnections);
    }
    return isSameGeometricRectangles;
  }

  private isRailArticlesBetweenConnections(previousCube: Connection, currentCube: Connection) {
    const adapterParents = this.articleConnectionService.getBackwardConnectedArticlesOnRail(
      [previousCube.articleContainer.articleUsage.id, currentCube.articleContainer.articleUsage.id]);

    /* in case the label is on a vertical adapter, sitting on only one article, previous- & currentCube
    are on the same articleContainer so they cannot have any articles between them */
    if(adapterParents.length < 2) return false;

    return this.createLabelingHelper.collectArticlesBetweenContainers(adapterParents[0], adapterParents[1], false).length > 0;
  }

  private hasCollidingConnection(acceptingConnections: Connection[]): boolean {
    const sortedConnections: Connection[] = ConnectionInfoUtils.sortConnections(acceptingConnections, false);
    const firstConnection: Connection = sortedConnections[0];
    const lastConnection: Connection = sortedConnections[sortedConnections.length - 1];

    const collisionConnections: Connection[] = CollisionHelper.findCollisionConnectionsInGlobal(
      firstConnection,
      lastConnection,
      this.containerService
    );
    for (const collisionConnection of collisionConnections) {
      const collisionConnectionToCheck: Connection =
        ConnectionInfoUtils.findAcceptingMultipurposeCubeByTextureCube(collisionConnection) || collisionConnection;
      if (
        !(collisionConnectionToCheck.metaElementVO instanceof ConnectionAreaVO) ||
        collisionConnectionToCheck.metaElementVO.connectionSide !== ConnectionAreaVO.SIDE_ACCEPTING
      ) {
        continue;
      }

      if (sortedConnections.indexOf(collisionConnectionToCheck) < 0
        && !ConnectionInfoUtils.isFreeConnection(collisionConnectionToCheck, false)) {
        return true;
      }
    }

    return false;
  }

  protected isSameGeometricRectangles(previousRectangle: Rectangle3D, currentRectangle: Rectangle3D): boolean {
    return MathUtils.isEqualsVectorsByTwoAxis(previousRectangle.point1, currentRectangle.point1);
  }

  private isEndPlatesBetweenArticles(
    previousArticle: ArticleContainer,
    currentArticle: ArticleContainer,
    distanceBetweenNeighborConnections: number
  ): boolean {

    if (
      !this.articleConnectionService.isArticleConnectedToMountingRail(previousArticle) ||
      !this.articleConnectionService.isArticleConnectedToMountingRail(currentArticle)
    ) {
      return false;
    }

    const insertArticleHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (!insertArticleHelper) {
      return false;
    }

    let result: boolean = false;
    const containers: ArticleContainer[] = insertArticleHelper.getConnectedArticlesBetweenContainers(previousArticle, currentArticle);
    const firstArticleLine: AdvancedLine = insertArticleHelper.getArticleLine(containers[0]);
    let previousArticleLine: AdvancedLine = firstArticleLine;
    let secondArticleLine: AdvancedLine = null;
    for (let i: number = 0; i < containers.length - 1; i++) {
      const nextArticleContainer: ArticleContainer = containers[i + 1];
      const nextArticleIsEndPlateArticle: boolean = ArticleUtils.isEndPlateArticle(nextArticleContainer.article);
      if (!nextArticleIsEndPlateArticle && i + 2 !== containers.length) {
        // every article encountered in-between (so excluding last article) must be an endplate
        return false;
      }
      secondArticleLine = insertArticleHelper.getArticleLine(nextArticleContainer);
      if (MathUtils.compare(previousArticleLine.end.x, secondArticleLine.start.x) === 0 ||
        MathUtils.compare(previousArticleLine.start.x, secondArticleLine.end.x) === 0) {
        result = true;
        previousArticleLine = secondArticleLine;
      } else {
        return false;
      }
    }
    return result &&
      (MathUtils.compare(firstArticleLine.end.distanceTo(secondArticleLine.start),
          distanceBetweenNeighborConnections, AcceptingConnectionSortGroupHelper.EPSILON) === 0 ||
        MathUtils.compare(firstArticleLine.start.distanceTo(secondArticleLine.end), distanceBetweenNeighborConnections,
          AcceptingConnectionSortGroupHelper.EPSILON) === 0);
  }

  private isLabelingAcrossOverlapAllowed(previousArticle: ArticleContainer, currentArticle: ArticleContainer): boolean {
    if (!ArticleUtils.isLabelingAcrossOverlapAllowed(previousArticle.article) ||
      !ArticleUtils.isLabelingAcrossOverlapAllowed(currentArticle.article)) {
      return false;
    }
    if (!this.articleConnectionService.isArticleConnectedToMountingRail(previousArticle) ||
      !this.articleConnectionService.isArticleConnectedToMountingRail(currentArticle)) {
      return false;
    }
    const insertArticleHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (insertArticleHelper) {
      const previousArticleLine: AdvancedLine = insertArticleHelper.getArticleLine(previousArticle);
      const currentArticleLine: AdvancedLine = insertArticleHelper.getArticleLine(currentArticle);
      const maxOverlapLength: number = AdvancedLine.calculateMaxOverlapLength(previousArticleLine, currentArticleLine);
      return previousArticleLine.hasPoint(currentArticleLine.start) &&
        MathUtils.compare(previousArticleLine.end.distanceTo(currentArticleLine.start), maxOverlapLength, AcceptingConnectionSortGroupHelper.EPSILON) === 0;
    }
  }
}
